import { assertDimensionQuantity } from "@workspace/api-zod/quantity-dimensions";
import { recipeAtQuotePrice, quoteRecipeSplit } from "@/components/quote-item-recipe-breakdown";
import { recipeForQuoteSnapshot } from "@workspace/api-zod/cost-recipe";

/** Saved rows are authoritative: never reclassify, reprice or match on opening. */
export function savedQuoteEditorState(quote: any) {
  const lines = (quote.lineItems ?? []).map((line: any) => ({
    ...line,
    description: line.description ?? "",
    quantity: Number(line.quantity),
    unitCost: Number(line.unitCost),
    labourHours: Number(line.labourHours ?? 0),
    labourCost: Number(line.labourCost ?? 0),
    materialCost: Number(line.materialCost ?? 0),
    plantCost: Number(line.plantCost ?? 0),
  }));
  const percent = (amount: unknown) => Number(quote.subtotal) > 0
    ? String(Number(amount ?? 0) / Number(quote.subtotal) * 100) : "0";
  const scopes = [...new Set<string>([
    ...(quote.scopeNames ?? []), ...lines.map((line: any) => line.scopeName).filter(Boolean),
  ])].filter(name => name !== "__shared__");
  return {
    lines,
    contingencyPercent: percent(quote.contingencyAmount),
    overheadPercent: percent(quote.overheadAmount),
    profitPercent: percent(quote.profitAmount),
    analysis: {
      projectType: quote.projectType ?? "general_works",
      projectTypes: [quote.projectType ?? "general_works"],
      projectSummary: quote.description ?? "",
      lineItems: lines,
      assumptions: (quote.assumptions ?? "").split("\n").filter(Boolean),
      exclusions: (quote.exclusions ?? "").split("\n").filter(Boolean),
      estimatedDays: quote.estimatedDays ?? 0,
      confidenceLevel: quote.confidenceLevel ?? "medium",
      notes: "",
      reviewerNotes: quote.reviewerNotes ?? [],
      validationFlags: quote.validationFlags ?? [],
      isMultiScope: !!quote.isMultiScope,
      scopes: scopes.map(name => ({ name, description: "" })),
    },
  };
}

export function savedQuoteEditLines(lines: any[], originalLines: any[]) {
  const originals = new Map(originalLines.map(line => [line.id, line]));
  return lines.map((line, index) => {
    if (line.quantityDerivation) assertDimensionQuantity(line.quantityDerivation, Number(line.quantity), line.unit);
    const original = line.id ? originals.get(line.id) : undefined;
    const recipe = line.quoteCostRecipe
      ? recipeForQuoteSnapshot(recipeAtQuotePrice(line.quoteCostRecipe, Number(line.unitCost))) : null;
    const split = recipe ? quoteRecipeSplit(recipe, Number(line.quantity)) : null;
    return {
      ...line,
      ...(original && original.description !== line.description ? { customerDescription: null } : {}),
      quantity: String(line.quantity),
      unitCost: String(line.unitCost),
      labourHours: String(line.labourHours ?? 0),
      labourCost: String(split?.labourCost ?? line.labourCost ?? 0),
      materialCost: String(split?.materialCost ?? line.materialCost ?? 0),
      plantCost: String(split?.plantCost ?? line.plantCost ?? 0),
      quoteCostRecipe: recipe,
      sortOrder: index,
    };
  });
}
